/**
 * Service Stripe côté client
 */

import { useAuthStore } from '../stores/authStore';

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Récupère le token d'authentification
 */
function getAuthToken(): string | null {
  return useAuthStore.getState().token;
}

/**
 * Headers avec authentification
 */
function getAuthHeaders(): HeadersInit {
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

interface CheckoutSessionResponse {
  url: string;
}

interface SubscriptionInfo {
  status: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  plan: 'monthly' | 'yearly';
}

/**
 * Crée une session Stripe Checkout et redirige vers la page de paiement
 */
export async function createCheckoutSession(
  plan: 'monthly' | 'yearly'
): Promise<void> {
  try {
    const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ priceType: plan }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erreur lors de la création du paiement');
    }
    
    const { url } = await response.json() as CheckoutSessionResponse;
    
    // Rediriger vers Stripe Checkout
    window.location.href = url;
  } catch (error) {
    console.error('Erreur Stripe Checkout:', error);
    throw error;
  }
}

/**
 * Récupère les informations d'abonnement
 */
export async function getSubscriptionInfo(): Promise<SubscriptionInfo | null> {
  try {
    const response = await fetch(`${API_URL}/api/stripe/subscription`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    
    if (!response.ok) {
      return null;
    }
    
    return await response.json() as SubscriptionInfo;
  } catch (error) {
    console.error('Erreur récupération abonnement:', error);
    return null;
  }
}

/**
 * Annule l'abonnement à la fin de la période
 */
export async function cancelSubscription(): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/api/stripe/cancel-subscription`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    
    return response.ok;
  } catch (error) {
    console.error('Erreur annulation abonnement:', error);
    return false;
  }
}

/**
 * Ouvre le portail client Stripe pour gérer l'abonnement
 */
export async function openCustomerPortal(): Promise<void> {
  try {
    const response = await fetch(`${API_URL}/api/stripe/customer-portal`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Erreur lors de l\'ouverture du portail');
    }
    
    const { url } = await response.json();
    window.location.href = url;
  } catch (error) {
    console.error('Erreur portail client:', error);
    throw error;
  }
}
